import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { cn, Dialog, DialogContent, DialogTitle, DialogTrigger } from "ui";
import { type ExerciseThumbnail } from "utils";

import { ExerciseImageCarousel } from "src/components/ExercisePreviewImageCarousel";
import { VideoPreview } from "src/components/ExerciseVideoPreview";

type Thumbnail = ExerciseThumbnail;

interface Exercise {
  id: string;
  name: string;
  videoURL?: string;
  images: {
    url: string;
  }[];
  previewImages: {
    url: string;
  }[];
  uploadedVideo?: {
    url: string;
  } | null;
  thumbnail: Thumbnail;
}

interface Props {
  exercise: Exercise;
}

export const ExercisePreviewDialog = observer(function ExercisePreviewDialog(
  props: React.PropsWithChildren<Props>,
) {
  const thumbnail = props.exercise.thumbnail;
  const hasVideo = thumbnail != null && thumbnail.type !== "image";
  const hasImages = props.exercise.previewImages.length > 0;
  const [activeThingie, setActiveThingie] = React.useState<"video" | number>(
    hasImages ? 0 : hasVideo ? "video" : 0,
  );
  const [open, setOpen] = React.useState(false);

  const activeImage =
    typeof activeThingie === "number"
      ? props.exercise.previewImages[activeThingie]
      : null;
  const hasMultipleImages = props.exercise.previewImages.length > 1;
  const hasMediaSwitcher = hasVideo && hasImages;

  function handleImageSelect(index: number) {
    setActiveThingie(index);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{props.children}</DialogTrigger>
      <DialogContent
        className="max-h-[90vh]"
        onOverlayClick={() => {
          setOpen(false);
        }}
      >
        <DialogTitle>{props.exercise.name}</DialogTitle>
        {activeImage &&
          hasMultipleImages &&
          typeof activeThingie === "number" && (
            <ExerciseImageCarousel
              images={props.exercise.previewImages}
              activeIndex={activeThingie}
              onSelect={handleImageSelect}
            />
          )}
        {activeImage && !hasMultipleImages && (
          <div className="max-h-[60vh]">
            <img
              src={activeImage.url}
              className="h-full w-full object-contain"
            />
          </div>
        )}
        {activeThingie === "video" && (
          <>
            {hasVideo ? (
              <VideoPreview thumbnail={thumbnail} />
            ) : (
              <div className="text-center">No video available</div>
            )}
          </>
        )}
        {hasMediaSwitcher && (
          <div className="flex items-center gap-2">
            <div
              className={cn(
                "rounded-[4px] w-10 h-10 border flex items-center justify-center",
                {
                  "outline outline-active-primary":
                    typeof activeThingie === "number",
                },
              )}
              onClick={() => {
                setActiveThingie(0);
              }}
            >
              <Icon name="tabler-photo" className="w-5 h-5" />
            </div>
            <div
              className={cn(
                "rounded-[4px] w-10 h-10 border flex items-center justify-center",
                {
                  "outline outline-active-primary": activeThingie === "video",
                },
              )}
              onClick={() => {
                setActiveThingie("video");
              }}
            >
              <Icon
                name="tabler-filled-player-play"
                className="w-5 h-5 stroke-primary fill-primary"
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
});
export interface ExerciseImageCarouselProps {
  images: {
    url: string;
  }[];
  activeIndex: number;
  onSelect: (index: number) => void;
}
export interface VideoPreviewProps {
  thumbnail: Extract<
    NonNullable<Thumbnail>,
    { type: "uploaded-video" | "youtube" | "vimeo" }
  >;
}
